import { useEffect, useMemo, type ReactNode } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import '../styles/faq.css';

interface FaqSection {
  title: string;
  description?: string;
  items: string[];
  mediaPlaceholders?: Array<string | ReactNode>;
}

const marketplaceSections: FaqSection[] = [
  {
    title: 'Marketplace essentials',
    items: [
      'Use marketplace search to match titles, descriptions, tags, authors, and JSON content. Combine tag, status, and date filters when you need a focused shortlist.',
      'Toggle the entry type pill (Flows vs Nodes) and the Created/Updated range selector to quickly surface recently changed work.',
      'Open a flow detail page to review ownership, approvals, downloadable assets, and the timeline. Download flows to import into Identity Orchestration—placeholders below show the import steps.',
      'Node cards include the JSON snippet. Copy the node payload and paste it straight into the Identity Orchestration designer. The second placeholder outlines that copy/paste animation.',
      'Watch an entry when you want notification parity with the author. The watch button lives near the title, and you can manage preferences later from Account Preferences.'
    ],
    mediaPlaceholders: [<img src="/media/node-paste.gif" alt="Node copy & paste animation" />]
  },
  {
    title: 'Creating or updating entries',
    items: [
      'Click New Entry to start a draft. Paste JSON or upload a file—auto-detection classifies the flow kind, library type, and primary capability to reduce manual tagging.',
      'Add supporting context (summary, tags, prerequisites) so reviewers understand what the JSON is doing. The preview panel highlights required fields while you type.',
      'Use the date range selector to communicate when the entry should be available in the marketplace. You can edit these windows later without resubmitting the JSON.',
      'Upload an optional tile image to make the marketplace gallery easier to scan, especially for high-signal flows that deserve visual emphasis.',
      'When editing, include a concise revision note. The audit log shows reviewers exactly what changed between versions.'
    ]
  },
  {
    title: 'Node designer workspace',
    items: [
      'Visit the Designer tab to prototype HTML, CSS, and JavaScript-based nodes. Toggle code panels, refresh the preview, or pop it into a new tab when you need more room.',
      'Use the import modal to paste marketplace node JSON back into the designer. Edit it visually and export as either a web node or mailer node—exports land on your clipboard for easy sharing.',
      'Mailer exports automatically inline CSS and strip JavaScript. Web node exports return the original HTML/CSS/JS blocks without additional wrappers.'
    ]
  },
  {
    title: 'Notifications & preferences',
    items: [
      'Account Preferences lets you tailor email alerts by entry type: ones you create, participate in, or explicitly watch. You can set different toggles for marketplace entries and forum threads.',
      'Watching an entry or forum thread gives you the same notification coverage as the author. Use the watch buttons when you want to track a discussion without commenting.',
      'Admins also receive optional alerts when a new marketplace submission is pending review—disable or re-enable the email at any time from Account Preferences.'
    ]
  },
  {
    title: 'Community forum',
    items: [
      'Use the Forum tab to ask questions, propose improvements, or document integration gotchas. Threads support tags, status filters, and category hierarchies.',
      'Replies can be edited or deleted (with tombstones) and authors can mark accepted answers. Admins can pin, lock, move, or reopen threads to keep knowledge organized.',
      'Watch a thread to receive notifications without replying. Use tags and status toggles (open, answered, closed) to find authoritative answers quickly.'
    ]
  }
];

const requestsSection: FaqSection = {
  title: 'Flow & node requests',
  items: [
    'Use the Flow & Node Requests tab in the forum to propose new flows/nodes, vote on community needs, search titles/descriptions/authors, and filter by “My requests” or “Hide completed”.',
    'Statuses communicate progress: Open, Under development, and Completed (with a marketplace link). Completed items automatically drop to the bottom unless you filter for them.',
    'Authors can edit or withdraw requests only while they are open and vote-free. After votes accumulate or statuses change, admins manage updates and can add the marketplace link when work ships.',
    'Authors and voters automatically receive notifications when admins change the status or when a request is withdrawn, so everyone stays in sync.'
  ]
};

const contentSections: FaqSection[] = [
  {
    title: 'Writing strong descriptions & JSON',
    items: [
      'Keep titles discoverable and action oriented. Describe the user goal in the summary and add prerequisites or caveats to the dedicated notes fields.',
      'Rapidly skim the JSON editor with the built-in validation. Prefer explicit key names, snake_case properties, and stable identifiers so downstream teams can reuse the payload safely.',
      'Revision notes help reviewers understand what changed. Mention behavioural updates, new integrations, or risk considerations to streamline approvals.',
      'Attach representative screenshots or tile imagery when it clarifies the experience—visual context speeds up triage.'
    ]
  }
];

const helpSection: FaqSection = {
  title: 'Need help?',
  items: [
    'Open a forum thread for collaborative problem solving—link to the entry using the copy URL button on the flow page so the community can reproduce issues.',
    'If you need direct assistance, contact the platform enablement team or submit a support ticket with the flow ID, JSON payload, and screenshots.',
    'For rollout questions, pair marketplace discussions with the notifications dashboard so stakeholders know who is watching each entry.'
  ]
};

const adminSection: FaqSection = {
  title: 'Admin toolkit',
  description:
    'These capabilities are only visible when you are signed in with an admin role.',
  items: [
    'Use the Admin Dashboard for live metrics: totals, top downloads/copies, recent activity, and per-user audit timestamps (last login, download, copy, upload, forum touchpoints).',
    'Visit the Pending tab to review submissions. Add approval notes to capture compliance context. You can auto-approve your own entries when appropriate.',
    'On flow detail pages, admins can update request statuses, reset recommendations, or clear watchlists. Use these tools to keep the marketplace curated.',
    'Account Preferences includes an extra toggle for pending-entry email alerts. Enable it if you want an inbox ping whenever new content needs review.',
    'Forum posts come with moderation controls—pin, lock, move, or delete replies to keep knowledge organized and safe.'
  ]
};

function renderSection(section: FaqSection, extraClassName = '') {
  const className = extraClassName
    ? `faq__section ${extraClassName}`
    : 'faq__section';
  return (
    <section key={section.title} className={className}>
      <h2>{section.title}</h2>
      {section.description ? <p className="faq__section-lead">{section.description}</p> : null}
      <ul>
        {section.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      {section.mediaPlaceholders?.map((media, index) =>
        typeof media === 'string' ? (
          <div key={`${section.title}-media-${index}`} className="faq__media-placeholder">
            <span>{media}</span>
          </div>
        ) : (
          <div key={`${section.title}-media-${index}`} className="faq__media-item">
            {media}
          </div>
        )
      )}
    </section>
  );
}

export function FaqPage() {
  const { user, refreshUser } = useAuth();
  const isAdmin = user?.role === 'admin';
  const sections = useMemo(() => [requestsSection, ...marketplaceSections, ...contentSections], []);

  useEffect(() => {
    if (!user || user.hasVisitedFaq) {
      return;
    }
    const markVisited = async () => {
      try {
        await api.markFaqVisited();
        await refreshUser();
      } catch (error) {
        console.error('Failed to mark FAQ as visited', error);
      }
    };
    void markVisited();
  }, [user, refreshUser]);

  return (
    <div className="faq">
      <h1>FAQ & Best Practices</h1>
      <p className="faq__intro">
        Everything you need to make the most of the IO Marketplace—from discovering reusable flows
        and prototyping nodes to collaborating through the forum and keeping notifications tidy.
      </p>

      {sections.map((section) => renderSection(section))}

      {isAdmin ? renderSection(adminSection, 'faq__section--admin') : null}

      {renderSection(helpSection)}
    </div>
  );
}
